import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { EmptyState, ErrorState, SkeletonList, SkeletonTable } from "./states";

export type SortDirection = "asc" | "desc";

export type DataColumn<Row> = {
  id: string;
  /** Column heading. Keep it short; `label` carries the spoken form. */
  header: React.ReactNode;
  cell: (row: Row) => React.ReactNode;
  /**
   * Plain-text heading used as the term in the mobile card and as the
   * accessible name of the sort control.
   */
  label?: string;
  /** The identifying column. It carries the row link on both layouts. */
  primary?: boolean;
  /** Kept out of the mobile card to protect the small-screen reading order. */
  hideOnMobile?: boolean;
  align?: "start" | "end";
  /** Utility classes for the column width, applied to header and cells. */
  className?: string;
  sortable?: boolean;
};

export type DataViewProps<Row> = {
  /** Names the table for assistive technology. Required. */
  caption: string;
  columns: DataColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => React.Key;
  /**
   * Makes each row open a record. The link is a real anchor so it can be
   * opened in a new tab, and it is one tab stop per row. Rendering a link never
   * implies access: the server still authorises the target route.
   */
  getRowHref?: (row: Row) => string;
  /** Buttons or a menu pinned to the end of the row, above the row link. */
  rowActions?: (row: Row) => React.ReactNode;
  sort?: { columnId: string; direction: SortDirection };
  onSortChange?: (columnId: string) => void;
  status?: "ready" | "loading" | "error";
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
  /** Replaces the default "nothing here yet" message. */
  empty?: React.ReactNode;
  density?: "comfortable" | "compact";
  className?: string;
};

function SortIndicator({
  active,
  direction,
}: {
  active: boolean;
  direction?: SortDirection;
}) {
  if (!active) {
    return (
      <ChevronsUpDown
        aria-hidden="true"
        className="h-3.5 w-3.5 opacity-0 transition-opacity duration-fast group-hover/sort:opacity-60"
      />
    );
  }
  return direction === "asc" ? (
    <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
  ) : (
    <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
  );
}

/**
 * One list of records rendered two ways: a table from `md` up, stacked cards
 * below it. Both layouts are generated from the same column model, so a screen
 * cannot drift between them.
 */
export function DataView<Row>({
  caption,
  columns,
  rows,
  getRowKey,
  getRowHref,
  rowActions,
  sort,
  onSortChange,
  status = "ready",
  errorTitle,
  errorDescription,
  onRetry,
  empty,
  density = "comfortable",
  className,
}: DataViewProps<Row>) {
  const primaryColumn = columns.find((column) => column.primary) ?? columns[0];
  const mobileColumns = columns.filter(
    (column) => !column.hideOnMobile && column.id !== primaryColumn?.id,
  );
  const cellPadding = density === "compact" ? "py-2" : "py-3";

  if (status === "loading") {
    return (
      <div className={cn("rounded-lg border bg-background", className)}>
        <div className="hidden md:block">
          <SkeletonTable columns={columns.length} label={`Loading ${caption}`} />
        </div>
        <div className="p-3 md:hidden">
          <SkeletonList label={`Loading ${caption}`} />
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={cn("rounded-lg border bg-background", className)}>
        <ErrorState
          title={errorTitle}
          description={errorDescription}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={cn("rounded-lg border bg-background", className)}>
        {empty ?? <EmptyState description={`No ${caption.toLowerCase()} to show.`} />}
      </div>
    );
  }

  return (
    <div className={cn("min-w-0", className)}>
      {/* Table layout: tablet and desktop. */}
      <div className="hidden overflow-hidden rounded-lg border bg-background md:block">
        <Table>
          <caption className="sr-only">{caption}</caption>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {columns.map((column) => {
                const active = sort?.columnId === column.id;
                const sortable = Boolean(column.sortable && onSortChange);

                return (
                  <TableHead
                    key={column.id}
                    scope="col"
                    aria-sort={
                      sortable
                        ? active
                          ? sort?.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                        : undefined
                    }
                    className={cn(
                      column.align === "end" && "text-right",
                      column.className,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange?.(column.id)}
                        className="group/sort -mx-1 inline-flex min-h-target items-center gap-1 rounded-sm px-1 uppercase hover:text-foreground"
                      >
                        {column.header}
                        <SortIndicator active={active} direction={sort?.direction} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
              {rowActions && (
                <TableHead scope="col" className="w-px text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const href = getRowHref?.(row);

              return (
                <TableRow key={getRowKey(row)} className="relative">
                  {columns.map((column) => {
                    const content = column.cell(row);

                    return (
                      <TableCell
                        key={column.id}
                        className={cn(
                          cellPadding,
                          column.align === "end" && "text-right",
                          column.className,
                        )}
                      >
                        {href && column.id === primaryColumn?.id ? (
                          // The anchor is stretched across the row, so the row
                          // is one tab stop and still a real link.
                          <Link
                            to={href}
                            className="font-medium after:absolute after:inset-0 after:content-['']"
                          >
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </TableCell>
                    );
                  })}
                  {rowActions && (
                    <TableCell className={cn(cellPadding, "text-right")}>
                      {/* Sits above the stretched row link. */}
                      <div className="relative z-10 flex justify-end gap-1">
                        {rowActions(row)}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Card layout: phones. */}
      <ul aria-label={caption} className="flex flex-col gap-2 md:hidden">
        {rows.map((row) => {
          const href = getRowHref?.(row);

          return (
            <li
              key={getRowKey(row)}
              className="relative rounded-lg border bg-background p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm font-medium">
                  {href ? (
                    <Link
                      to={href}
                      className="after:absolute after:inset-0 after:content-['']"
                    >
                      {primaryColumn?.cell(row)}
                    </Link>
                  ) : (
                    primaryColumn?.cell(row)
                  )}
                </div>
                {rowActions && (
                  <div className="relative z-10 flex shrink-0 gap-1">
                    {rowActions(row)}
                  </div>
                )}
              </div>

              {mobileColumns.length > 0 && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  {mobileColumns.map((column) => (
                    <React.Fragment key={column.id}>
                      <dt className="text-muted-foreground">
                        {column.label ?? column.header}
                      </dt>
                      <dd className="min-w-0 text-foreground">
                        {column.cell(row)}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export type DataPaginationProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Names the control when a screen paginates more than one list. */
  label?: string;
  className?: string;
};

/**
 * Previous/next paging. The position is a live region so a screen-reader user
 * hears the page change without the buttons losing focus.
 */
export function DataPagination({
  page,
  pageCount,
  onPageChange,
  label = "Pagination",
  className,
}: DataPaginationProps) {
  if (pageCount <= 1) return null;

  return (
    <nav
      aria-label={label}
      className={cn("flex items-center justify-between gap-3", className)}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </Button>
      <p aria-live="polite" className="text-xs text-muted-foreground">
        Page {page} of {pageCount}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </nav>
  );
}
